/* eslint-disable no-plusplus, no-bitwise */

import { createContext } from '@hover/blueprint';
import type { PropsWithChildren } from 'react';
import React, { useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import invariant from 'tiny-invariant';

import { isServer } from '../../utils/environment';
import { useNonce } from '../useNonce';

interface PartnerProps extends PropsWithChildren<{}> {}

export interface RandomContext {
  random: () => number;
}

/**
 * Seed generator so that we use well-distributed
 * seeds for random number generation
 *
 * @see {@link github.com/bryc/code/blob/master/jshash/PRNGs.md#addendum-a-seed-generating-functions}
 *
 * @param string string to hash
 *
 * @returns 32-bit seed
 */
const xmur3 = (string: string) => {
  let h = 1779033703 ^ string.length;

  for (let i = 0; i < string.length; i++) {
    h = Math.imul(h ^ string.charCodeAt(i), 3432918353);
    h = (h << 13) | (h >>> 19);
  }

  return () => {
    h = Math.imul(h ^ (h >>> 16), 2246822507);
    h = Math.imul(h ^ (h >>> 13), 3266489909);

    h ^= h >>> 16;

    return h >>> 0;
  };
};

/**
 * Seeded random number generator
 *
 * @see {@link https://github.com/bryc/code/blob/master/jshash/PRNGs.md#sfc32}
 *
 * @param a 32-bit seed
 * @param b 32-bit seed
 * @param c 32-bit seed
 * @param d 32-bit seed
 *
 * @returns random number generation function
 */
const sfc32 = (a: number, b: number, c: number, d: number) => {
  // Be obvious that we're using the closure for state
  let [A, B, C, D] = [a, b, c, d];

  return () => {
    const t = (((A + B) | 0) + D) | 0;

    D = (D + 1) | 0;
    A = B ^ (B >>> 9);
    B = (C + (C << 3)) | 0;
    C = (C << 21) | (C >>> 11);
    C = (C + t) | 0;

    return (t >>> 0) / 4294967296;
  };
};

const [Provider, useContext] = createContext<RandomContext>({
  strict: true,
  name: 'RandomContext',
});

/**
 * Hook to retrieve an isomorphic random number generator function that is
 * seeded the same on both the server and the client. This allows us to use
 * random numbers in server-rendered content that will still match the client
 * upon hydration.
 *
 * @remarks Like `Math.random()`, this is a pseudo-random number generator, so
 * it is not cryptographically secure.
 *
 * @example
 * ```ts
 * const random = useRandom();
 *
 * random(); // Effectively equivalent to Math.random()
 * ```
 *
 * @returns random number generation function
 */
const useRandom = () => useContext().random;

const RandomProvider = ({ children }: PartnerProps) => {
  const nonce = useNonce();

  // eslint-disable-next-line no-underscore-dangle
  const seed = isServer() ? Date.now().toString() : window.__randomSeed__;

  invariant(
    seed,
    'useRandom: seed missing from server render pass, check `window.__randomSeed__`',
  );

  const random = useMemo(() => {
    const generate = xmur3(seed);
    return sfc32(generate(), generate(), generate(), generate());
  }, [seed]);

  return (
    <>
      {isServer() && (
        <Helmet>
          <script nonce={nonce}>{`window.__randomSeed__ = "${seed}"`}</script>
        </Helmet>
      )}
      <Provider value={{ random }}>{children}</Provider>
    </>
  );
};

export { useRandom, RandomProvider };
